import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  InputAdornment,
  Stack,
} from "@mui/material";
import {
  FaArrowLeft,
  FaShieldAlt,
  FaExclamationTriangle,
} from "react-icons/fa";
import { toast } from "react-toastify";

import liteIcon from "../assets/icons/LiteIcon.png";
import { AuthContext } from "../AuthContext";
import { API_URL } from "../config";

/* ─── Floating particle background ─── */
const Particle = ({ style }) => (
  <motion.div
    style={{
      position: "absolute",
      borderRadius: "50%",
      background:
        "radial-gradient(circle, rgba(2,136,209,0.18) 0%, transparent 70%)",
      pointerEvents: "none",
      ...style,
    }}
    animate={{ y: [0, -18, 0], opacity: [0.5, 1, 0.5] }}
    transition={{
      duration: style.duration,
      repeat: Infinity,
      ease: "easeInOut",
      delay: style.delay,
    }}
  />
);

const particles = [
  { width: 320, height: 320, top: "-8%", right: "-6%", duration: 7, delay: 0 },
  {
    width: 200,
    height: 200,
    bottom: "8%",
    left: "3%",
    duration: 9,
    delay: 1.5,
  },
  { width: 140, height: 140, top: "40%", left: "15%", duration: 6, delay: 0.8 },
  {
    width: 100,
    height: 100,
    bottom: "20%",
    right: "12%",
    duration: 8,
    delay: 2,
  },
];

/* ─── Input field styles ─── */
const inputSx = {
  "& .MuiOutlinedInput-root": {
    color: "#1e293b",
    borderRadius: "14px",
    background: "#f8fafc",
    fontSize: "0.95rem",
    fontFamily: "'DM Sans', sans-serif",
    transition: "all 0.25s ease",
    "& fieldset": { borderColor: "#e2e8f0", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "#94a3b8" },
    "&.Mui-focused": {
      background: "#fff",
      boxShadow: "0 0 0 4px rgba(2,136,209,0.1)",
    },
    "&.Mui-focused fieldset": { borderColor: "#0288D1", borderWidth: "2px" },
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#94a3b8",
    fontSize: "0.9rem",
  },
};

/* ════════════════════════════════════════════════════════ */
const TLoginPage = () => {
  const [instCode, setInstCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [focused, setFocused] = useState(false);

  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Teacher Login | LiteUpper";
    // Load DM Sans font
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Sora:wght@700;800&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);

  const handleLogin = async () => {
    if (!instCode.trim()) {
      toast.error("Please enter your unique teacher code.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/login-T/`, {
        teacher_code: instCode,
      });
      const token = response.data.token;
      await login(token);
      localStorage.setItem("teacher_code", instCode);
      toast.success("Welcome back!");
      navigate("/instclass");
    } catch (error) {
      const message = error.response?.data?.detail || "Authentication failed.";
      if (
        error.response?.status === 403 &&
        message === "Account is not active"
      ) {
        navigate("/verification");
      } else if (
        error.response?.status === 403 &&
        message === "No active plan."
      ) {
        setModalMessage(
          "Your institution's subscription has expired. Please contact your administrator."
        );
        setModalOpen(true);
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleLogin();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(145deg, #f0f7ff 0%, #f8fafc 50%, #f0f4ff 100%)",
        position: "relative",
        overflow: "hidden",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {/* Noise texture overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.025,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          pointerEvents: "none",
        }}
      />

      {/* Particles */}
      {particles.map((p, i) => (
        <Particle key={i} style={p} />
      ))}

      {/* Subtle grid */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.035,
          backgroundImage:
            "linear-gradient(#0288D1 1px, transparent 1px), linear-gradient(90deg, #0288D1 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          pointerEvents: "none",
        }}
      />

      {/* Back button */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        style={{ position: "absolute", top: 24, left: 24, zIndex: 10 }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            color: "#475569",
            background: "rgba(255,255,255,0.9)",
            backdropFilter: "blur(8px)",
            boxShadow:
              "0 2px 12px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)",
            border: "1px solid rgba(255,255,255,0.8)",
            transition: "all 0.2s ease",
            "&:hover": {
              background: "#fff",
              color: "#0f172a",
              transform: "translateX(-2px)",
              boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
            },
          }}
        >
          <FaArrowLeft size={15} />
        </IconButton>
      </motion.div>

      <Container maxWidth="xs" sx={{ zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <Paper
            elevation={0}
            sx={{
              p: "40px 36px",
              borderRadius: "28px",
              textAlign: "center",
              background: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255,255,255,0.95)",
              boxShadow:
                "0 24px 64px -12px rgba(15,23,42,0.08), 0 0 0 1px rgba(2,136,209,0.06)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Top accent line */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: "15%",
                right: "15%",
                height: "2px",
                background:
                  "linear-gradient(90deg, transparent, #0288D1, #38bdf8, transparent)",
                borderRadius: "0 0 4px 4px",
              }}
            />

            {/* Logo */}
            <motion.div
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                duration: 0.5,
                delay: 0.15,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <Link to="/">
                <Box
                  component="img"
                  src={liteIcon}
                  alt="LiteUpper"
                  sx={{
                    width: 52,
                    height: "auto",
                    mb: 3,
                    filter: "drop-shadow(0 4px 12px rgba(2,136,209,0.25))",
                    transition: "transform 0.3s ease",
                    "&:hover": { transform: "scale(1.07)" },
                  }}
                />
              </Link>
            </motion.div>

            {/* Role badge */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.7,
                  px: 1.6,
                  py: 0.5,
                  borderRadius: "20px",
                  mb: 2,
                  background:
                    "linear-gradient(135deg, rgba(2,136,209,0.1), rgba(56,189,248,0.1))",
                  border: "1px solid rgba(2,136,209,0.18)",
                }}
              >
                <FaShieldAlt size={11} color="#0288D1" />
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: "#0288D1",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  Teacher Portal
                </Typography>
              </Box>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.4 }}
            >
              <Typography
                sx={{
                  color: "#0f172a",
                  fontWeight: 800,
                  fontSize: "1.65rem",
                  letterSpacing: "-0.7px",
                  lineHeight: 1.2,
                  mb: 0.8,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Welcome Back
              </Typography>
              <Typography
                sx={{
                  color: "#64748b",
                  mb: 3.5,
                  fontWeight: 500,
                  fontSize: "0.88rem",
                  lineHeight: 1.5,
                }}
              >
                Enter your teacher code to access your workspace
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.32, duration: 0.45 }}
            >
              <Stack spacing={2.5}>
                {/* Code input */}
                <Box sx={{ position: "relative" }}>
                  <TextField
                    fullWidth
                    placeholder="Enter Teacher Code"
                    value={instCode}
                    onChange={(e) => setInstCode(e.target.value.trim())}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    onKeyDown={handleKeyDown}
                    autoComplete="off"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <motion.div
                            animate={{ color: focused ? "#0288D1" : "#94a3b8" }}
                            transition={{ duration: 0.2 }}
                          >
                            <FaShieldAlt size={15} />
                          </motion.div>
                        </InputAdornment>
                      ),
                    }}
                    sx={inputSx}
                  />
                </Box>

                {/* Login button */}
                <motion.div
                  whileHover={{ scale: 1.015 }}
                  whileTap={{ scale: 0.985 }}
                >
                  <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    onClick={handleLogin}
                    disabled={loading}
                    sx={{
                      py: 1.75,
                      borderRadius: "14px",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      fontFamily: "'DM Sans', sans-serif",
                      letterSpacing: "-0.2px",
                      background: loading
                        ? "#e2e8f0"
                        : "linear-gradient(135deg, #0277bd 0%, #0288D1 50%, #0ea5e9 100%)",
                      boxShadow: loading
                        ? "none"
                        : "0 8px 24px -4px rgba(2,136,209,0.45)",
                      transition: "all 0.3s ease",
                      "&:hover:not(:disabled)": {
                        background:
                          "linear-gradient(135deg, #01579b 0%, #0277bd 100%)",
                        boxShadow: "0 12px 28px -4px rgba(2,136,209,0.55)",
                      },
                      "&:disabled": {
                        background: "#e2e8f0",
                        color: "#94a3b8",
                        boxShadow: "none",
                      },
                    }}
                  >
                    <AnimatePresence mode="wait">
                      {loading ? (
                        <motion.div
                          key="loading"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                          >
                            <CircularProgress
                              size={17}
                              thickness={5}
                              sx={{ color: "#94a3b8" }}
                            />
                            <span>Verifying...</span>
                          </Stack>
                        </motion.div>
                      ) : (
                        <motion.span
                          key="login"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          Sign In
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </Button>
                </motion.div>
              </Stack>
            </motion.div>

            {/* Divider hint */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <Box sx={{ mt: 3, pt: 2.5, borderTop: "1px solid #f1f5f9" }}>
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    color: "#94a3b8",
                    fontWeight: 500,
                  }}
                >
                  Contact your admin if you've lost your code
                </Typography>
              </Box>
            </motion.div>
          </Paper>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <Typography
            sx={{
              display: "block",
              textAlign: "center",
              mt: 3.5,
              color: "#b0bec5",
              fontWeight: 500,
              fontSize: "0.75rem",
              letterSpacing: "0.02em",
            }}
          >
            © {new Date().getFullYear()} LiteUpper · Secure Academic Portal
          </Typography>
        </motion.div>
      </Container>

      {/* Subscription Dialog */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        TransitionComponent={motion.div}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            p: "8px",
            maxWidth: "360px",
            textAlign: "center",
            fontFamily: "'DM Sans', sans-serif",
            boxShadow: "0 32px 64px -12px rgba(0,0,0,0.14)",
            border: "1px solid rgba(245,158,11,0.15)",
          },
        }}
      >
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                mx: "auto",
                mb: 2.5,
                background: "linear-gradient(135deg, #fff7e6, #fef3c7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 24px rgba(245,158,11,0.2)",
                border: "1px solid rgba(245,158,11,0.2)",
              }}
            >
              <FaExclamationTriangle size={28} color="#f59e0b" />
            </Box>
          </motion.div>
          <Typography
            sx={{
              fontWeight: 800,
              mb: 1,
              color: "#0f172a",
              fontSize: "1.1rem",
              fontFamily: "'Sora', sans-serif",
            }}
          >
            Subscription Expired
          </Typography>
          <Typography
            sx={{
              color: "#64748b",
              lineHeight: 1.65,
              fontSize: "0.88rem",
              fontWeight: 500,
            }}
          >
            {modalMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2.5, px: 3 }}>
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            style={{ width: "100%" }}
          >
            <Button
              onClick={() => setModalOpen(false)}
              variant="contained"
              fullWidth
              sx={{
                borderRadius: "12px",
                py: 1.3,
                background: "linear-gradient(135deg, #0f172a, #1e293b)",
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 700,
                textTransform: "none",
                fontSize: "0.9rem",
                boxShadow: "0 8px 20px rgba(15,23,42,0.2)",
                "&:hover": { background: "#1e293b" },
              }}
            >
              Understood
            </Button>
          </motion.div>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TLoginPage;
